import React, { useState } from 'react';
import { useToast } from '../../components/ui/use-toast';
import { Button } from '../../components/ui/button';
import { Loader2, Zap } from 'lucide-react';
import { Agent, TestResult } from '../../types/agent-types-new';
import { agentService } from '../../lib/api/agent-service';

interface TestingPlaygroundProps {
  agent: Agent | null | undefined;
}

type InputType = 'Text' | 'URL' | 'Image' | 'Page';

const TestingPlayground: React.FC<TestingPlaygroundProps> = ({ agent }) => {
  const { toast } = useToast();
  const [inputType, setInputType] = useState<InputType>('Text');
  const [inputContent, setInputContent] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  
  // Model settings
  const [model, setModel] = useState('gpt-4o');
  const [temperature, setTemperature] = useState(0.7);
  const [maxTokens, setMaxTokens] = useState(2000);
  
  /**
   * Get the actual agent type from the agent object
   * This looks for standard agent type identifiers and falls back to the MongoDB ID if none is found
   */
  const getAgentType = (agent: Agent | null | undefined): string => {
    if (!agent) return 'page-role'; // Default fallback
    
    // Use the ID directly if it's a standard agent type
    if (agent._id && [
      'page-role',
      'page-semantic-intent',
      'customer-intent', 
      'page-chatbot',
      'voice-chatbot',
      'adjacent-topics'
    ].includes(agent._id)) {
      return agent._id;
    }
    
    // If agent has an explicit agent_type field, use that
    if (agent.agent_type) {
      return agent.agent_type;
    }
    
    // Use the name as a more reliable identifier
    if (agent.name) {
      const name = agent.name.toLowerCase();
      
      if (name.includes('customer intent')) {
        return 'customer-intent';
      }
      
      if (name.includes('page semantic') || name.includes('semantic')) {
        return 'page-semantic-intent';
      }
      
      if (name.includes('page role') || name.includes('role')) {
        return 'page-role';
      }
      
      if (name.includes('chatbot') || name.includes('chat')) {
        return 'page-chatbot';
      }
    }
    
    // ONLY use category as last resort with these more reliable mappings
    if (agent.category) {
      switch (agent.category.toLowerCase()) {
        case 'intent':
          return 'customer-intent';
        case 'seo':
          return 'page-semantic-intent';
        case 'support':
          return 'page-chatbot';
        case 'content':
          return 'adjacent-topics';
        case 'research': // Be careful with this one - potentially misleading
          return 'site-crawler'; // Changed from 'competitive-analysis'
        case 'analytics':
          return 'auto-evaluator';
      }
    }
    
    // Fallback to page-role if we couldn't determine a type
    return 'page-role';
  };
  
  const handleRunTest = async () => {
    if (!agent) return;
    
    if (!inputContent.trim()) {
      toast({
        title: "Error",
        description: "Please enter test input",
        variant: "destructive",
      });
      return;
    }
    
    setIsLoading(true);
    setTestResult(null);
    
    try {
      // Prepare test input based on selected type
      const testInput: Record<string, any> = {
        model,
        temperature,
        maxTokens
      };
      
      // pageContent is always required by the API
      if (inputType === 'Text') {
        testInput.pageContent = inputContent;
      } else if (inputType === 'URL') {
        testInput.pageContent = `URL: ${inputContent}`;
      } else if (inputType === 'Image') {
        // For image type, we need both imagePath and pageContent (empty if needed)
        testInput.imagePath = inputContent;
        testInput.pageContent = "Image analysis request"; // Provide dummy content to satisfy API
      } else if (inputType === 'Page') {
        testInput.pageContent = inputContent;
      } else {
        // Fallback - ensure pageContent is never empty
        testInput.pageContent = inputContent || "Test content";
      }
      
      // Add session ID, page ID and trace ID
      const timestamp = Date.now();
      const randomSuffix = Math.floor(Math.random() * 1000);
      testInput.sessionId = 'test-session-' + timestamp;
      testInput.pageId = 'test-page-' + timestamp;
      // Add a properly formatted traceId that will work with our lookup endpoint
      testInput.traceId = `trace-${timestamp}-${randomSuffix}`;
      
      // Get agent_type from agent object if available, otherwise use _id as fallback
      // agent_type is what really matters for the test, not the MongoDB ObjectID
      const agentType = getAgentType(agent);
      console.log(`Testing agent type: ${agentType} (ID: ${agent._id})`);
      
      // Run the test with the agent type directly
      // Force customer-intent if this appears to be that agent type based on name
      const finalAgentType = agent.name?.toLowerCase().includes('customer intent') ? 'customer-intent' : agentType;
      console.log(`Testing agent type: ${finalAgentType} (mapped from ${agentType}, ID: ${agent._id})`);
      
      const result = await agentService.testAgent(finalAgentType, testInput);
      
      // No local copy of the run is stored: the backend wrote the agent_logs row for this
      // trace id (routes/agents-test.js:143-158) and the logs table reads it from the API.
      
      setTestResult(result);
      
      toast({
        title: "Test completed",
        description: "The agent test was executed successfully",
      });
    } catch (error) {
      console.error('Error running test:', error);
      toast({
        title: "Error",
        description: "Failed to run agent test",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="p-6">
      <div className="mb-6">
        <label className="block text-sm font-medium mb-2">Input Type</label>
        <div className="flex gap-2 flex-wrap">
          <Button 
            variant={inputType === 'Text' ? "default" : "outline"} 
            size="sm"
            onClick={() => setInputType('Text')}
          >
            Text
          </Button>
          <Button 
            variant={inputType === 'URL' ? "default" : "outline"} 
            size="sm"
            onClick={() => setInputType('URL')}
          >
            URL
          </Button>
          <Button 
            variant={inputType === 'Image' ? "default" : "outline"} 
            size="sm"
            onClick={() => setInputType('Image')}
          >
            Image
          </Button>
          <Button 
            variant={inputType === 'Page' ? "default" : "outline"} 
            size="sm"
            onClick={() => setInputType('Page')}
          >
            Page Content
          </Button>
        </div>
      </div>
      
      <div className="mb-6">
        <label className="block text-sm font-medium mb-2">
          {inputType === 'Text' && 'Text Input'}
          {inputType === 'URL' && 'URL to Process'}
          {inputType === 'Image' && 'Image Path'}
          {inputType === 'Page' && 'Page HTML Content'}
        </label>
        <textarea
          placeholder={
            inputType === 'Text' ? "Enter test text here..." :
            inputType === 'URL' ? "Enter URL to analyze..." :
            inputType === 'Image' ? "Enter image path..." :
            "Enter page HTML content..."
          }
          value={inputContent}
          onChange={(e) => setInputContent(e.target.value)}
          rows={8}
          className="w-full p-2 border rounded-md font-mono text-sm"
        />
      </div>
      
      <div className="mb-6">
        <label className="block text-sm font-medium mb-2">Model Settings</label>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Model</label>
            <select 
              value={model}
              onChange={(e) => setModel(e.target.value)}
              className="w-full p-2 text-sm border rounded-md"
            >
              <option value="gpt-4o">GPT-4o</option>
              <option value="gpt-4o-mini">GPT-4o Mini</option>
              <option value="claude-3-opus">Claude 3 Opus</option>
              <option value="claude-3-sonnet">Claude 3 Sonnet</option>
              <option value="claude-3-haiku">Claude 3 Haiku</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Temperature</label>
            <input 
              type="number" 
              value={temperature} 
              onChange={(e) => setTemperature(Number(e.target.value))}
              step="0.1" 
              min="0" 
              max="1" 
              className="w-full p-2 text-sm border rounded-md" 
            />
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Max Tokens</label>
            <input 
              type="number" 
              value={maxTokens}
              onChange={(e) => setMaxTokens(Number(e.target.value))}
              step="100" 
              className="w-full p-2 text-sm border rounded-md" 
            />
          </div>
        </div>
      </div>
      
      <Button 
        className="w-full" 
        onClick={handleRunTest}
        disabled={isLoading || !agent}
      >
        {isLoading ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Running Test...
          </>
        ) : (
          <>
            <Zap className="h-4 w-4 mr-2" />
            Run Test
          </>
        )}
      </Button>
      
      <div className="mt-8 border-t pt-6">
        <h3 className="font-medium mb-4">Results</h3>
        
        {testResult ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="border rounded-md p-3">
                <div className="text-xs text-gray-500 mb-1">Total Time</div>
                <div className="text-xl font-semibold">{testResult.timing.total.toFixed(2)}ms</div>
              </div>
              
              <div className="border rounded-md p-3">
                <div className="text-xs text-gray-500 mb-1">LLM Time</div>
                <div className="text-xl font-semibold">{testResult.timing.llm.toFixed(2)}ms</div>
              </div>
              
              <div className="border rounded-md p-3">
                <div className="text-xs text-gray-500 mb-1">Token Usage</div>
                <div className="text-xl font-semibold">{testResult.tokenUsage}</div>
              </div>
            </div>
            
            <div>
              <h4 className="text-sm font-medium mb-2">Prompt</h4>
              <div className="p-3 border rounded-md bg-gray-50 overflow-auto max-h-64">
                <pre className="text-xs whitespace-pre-wrap font-mono">
                  {testResult.prompt 
                    ? (typeof testResult.prompt === 'string' 
                        ? testResult.prompt 
                        : JSON.stringify(testResult.prompt, null, 2))
                    : 'No prompt available'}
                </pre>
              </div>
            </div>
            
            <div>
              <h4 className="text-sm font-medium mb-2">Output</h4>
              <div className="p-3 border rounded-md overflow-auto max-h-96">
                <pre className="text-sm whitespace-pre-wrap font-mono">
                  {typeof testResult.output === 'string' 
                    ? testResult.output 
                    : JSON.stringify(testResult.output, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-6 border rounded-md bg-muted text-center">
            <p className="text-muted-foreground">
              {isLoading ? 'Processing...' : 'Run a test to see results'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TestingPlayground;